import React, { type FC, type ReactNode, type ComponentProps, memo, useCallback, useState } from "react";
import { Button, type ButtonProps, Popover } from "@heroui/react";
import { FormattedMessage } from "react-intl";

type PopoverRootProps = ComponentProps<typeof Popover>;

type Props = Omit<PopoverRootProps, "children" | "title"> & {
    title: ReactNode;
    description?: ReactNode;
    trigger: ReactNode;
    onConfirm: () => void;
    confirmColor?: ButtonProps["variant"];
    confirmText?: ReactNode;
    showCancel?: boolean;
    triggerClassName?: string;
};

export const PopconfirmHero: FC<Props> = memo(
    ({
        title,
        description,
        trigger,
        onConfirm,
        confirmColor,
        confirmText,
        showCancel = true,
        isOpen: controlledIsOpen,
        onOpenChange: controlledOnOpenChange,
        triggerClassName,
        ...popoverProps
    }) => {
        const [internalIsOpen, setInternalIsOpen] = useState(false);

        const isOpen = controlledIsOpen ?? internalIsOpen;
        const setIsOpen = controlledOnOpenChange ?? setInternalIsOpen;

        const handleConfirm = useCallback(() => {
            onConfirm();
            setIsOpen(false);
        }, [onConfirm, setIsOpen]);
        const handleCancel = useCallback(() => setIsOpen(false), [setIsOpen]);

        return (
            <Popover isOpen={isOpen} onOpenChange={setIsOpen} {...popoverProps}>
                <Popover.Trigger tabIndex={-1} className={triggerClassName}>
                    {trigger}
                </Popover.Trigger>
                <Popover.Content placement="left" className="w-[240px] p-2 px-4">
                    <Popover.Arrow />
                    <Popover.Dialog aria-label="Confirmation" className="px-1 py-2 w-full">
                        <p className="font-bold text-foreground">{title}</p>
                        {description !== undefined ? (
                            description && <p>{description}</p>
                        ) : (
                            <p>
                                <FormattedMessage id="app.confirmation.body" />
                            </p>
                        )}
                        <div className="mt-2 flex gap-2 w-full">
                            <Button variant={confirmColor ?? "danger"} onPress={handleConfirm}>
                                {confirmText ?? <FormattedMessage id="app.button.confirm" />}
                            </Button>
                            {showCancel && (
                                <Button variant="ghost" onPress={handleCancel}>
                                    <FormattedMessage id="app.button.cancel" />
                                </Button>
                            )}
                        </div>
                    </Popover.Dialog>
                </Popover.Content>
            </Popover>
        );
    },
);
